import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  documentEvidenceTypes,
  type AthleteView,
  type CompetitionSeasonView,
  type DocumentEvidenceView,
  type PageResponse,
  type SupportedLocale,
} from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import {
  emptyEvidenceForm,
  evidenceCorrectionFormOf,
  evidenceCorrectionRequest,
  evidencePath,
  evidenceRequest,
  evidenceStatusKeys,
  evidenceTypeKeys,
  evidenceTypeLabelKey,
  pendingFirst,
  rejectRequest,
  verifyRequest,
  type EvidenceCorrectionForm,
  type EvidenceForm,
} from './documentEvidence';
import { RemoteSelect, type RemoteOption } from './RemoteSelect';

export interface EvidenceSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}`. */
  base: string;
  csrfToken: string;
  competition: CompetitionSeasonView;
}

type OpenPanel =
  | { kind: 'correct'; evidenceId: string; form: EvidenceCorrectionForm }
  | { kind: 'verify'; evidenceId: string; note: string }
  | { kind: 'reject'; evidenceId: string; reason: string };

/**
 * Documentary evidence of the athletes registered in one competition. A registrar records that a
 * document exists (type, issuer, days it is valid), then verifies or rejects it; only verified
 * evidence counts towards eligibility. A verified record can only be withdrawn, a rejected one stays
 * as history, and a replacement document is a new record. No copy of the document is stored.
 */
export function EvidenceSection({
  locale,
  base,
  csrfToken,
  competition,
}: EvidenceSectionProps): React.JSX.Element {
  const [records, setRecords] = useState<DocumentEvidenceView[] | null>(null);
  const [form, setForm] = useState<EvidenceForm>(emptyEvidenceForm);
  const [formKey, setFormKey] = useState(0);
  const [panel, setPanel] = useState<OpenPanel | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [message, setMessage] = useState<TranslationKey | null>(null);

  const load = useCallback(async () => {
    try {
      setRecords(
        await apiRequest<DocumentEvidenceView[]>(
          `${evidencePath(base)}?competitionSeasonId=${encodeURIComponent(competition.id)}`,
        ),
      );
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base, competition.id]);

  useEffect(() => {
    setRecords(null);
    setPanel(null);
    setErrorKey(null);
    setMessage(null);
    setForm(emptyEvidenceForm);
    setFormKey((current) => current + 1);
    void load();
  }, [load]);

  const findAthletes = useCallback(
    async (text: string): Promise<RemoteOption[]> => {
      const page = await apiRequest<PageResponse<AthleteView>>(
        `${base}/athletes?limit=20&q=${encodeURIComponent(text)}`,
      );
      return page.items.map((athlete) => ({
        id: athlete.id,
        label: `${athleteDisplayName(athlete, locale)} (${formatCalendarDate(athlete.dateOfBirth, locale)})`,
      }));
    },
    [base, locale],
  );

  async function submit(action: () => Promise<unknown>, successKey: TranslationKey): Promise<void> {
    setErrorKey(null);
    setMessage(null);
    try {
      await action();
      setMessage(successKey);
      setPanel(null);
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      // A version conflict or a record decided meanwhile: show what the server holds now.
      await load();
    }
  }

  const createRequest = evidenceRequest(form);

  async function record(): Promise<void> {
    if (createRequest === null) return;
    await submit(async () => {
      await apiRequest<DocumentEvidenceView>(evidencePath(base), {
        method: 'POST',
        body: createRequest,
        csrfToken,
      });
      setForm(emptyEvidenceForm);
      setFormKey((current) => current + 1);
    }, 'evidence.done');
  }

  async function command(
    evidence: DocumentEvidenceView,
    request: { method: 'PATCH' | 'POST'; action?: 'verify' | 'reject'; body: unknown },
    successKey: TranslationKey,
  ): Promise<void> {
    await submit(async () => {
      await apiRequest<DocumentEvidenceView>(evidencePath(base, evidence.id, request.action), {
        method: request.method,
        body: request.body,
        csrfToken,
        ifMatchVersion: evidence.version,
      });
    }, successKey);
  }

  function typeLabel(documentType: string): string {
    const key = evidenceTypeLabelKey(documentType);
    return key === null ? documentType : translate(locale, key);
  }

  const cancelButton = (
    <Button
      size="small"
      onClick={() => {
        setPanel(null);
      }}
    >
      {translate(locale, 'evidence.cancel')}
    </Button>
  );

  return (
    <Box component="section" aria-labelledby="evidence-title" sx={{ mt: 3 }}>
      <Typography variant="h6" component="h3" id="evidence-title" gutterBottom>
        {translate(locale, 'evidence.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'evidence.hint')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {message !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, message)}
        </Alert>
      )}
      {records === null && errorKey === null && (
        <Typography>{translate(locale, 'evidence.loading')}</Typography>
      )}
      {records?.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {translate(locale, 'evidence.empty')}
        </Typography>
      )}
      <Stack component="ul" spacing={2} sx={{ listStyle: 'none', padding: 0, margin: 0, mb: 3 }}>
        {pendingFirst(records ?? []).map((evidence) => {
          const open = panel?.evidenceId === evidence.id ? panel : null;
          const correction =
            open?.kind === 'correct' ? evidenceCorrectionRequest(open.form, evidence) : null;
          const rejection = open?.kind === 'reject' ? rejectRequest(open.reason) : null;
          return (
            <Box component="li" key={evidence.id}>
              <Typography variant="subtitle2">
                {athleteDisplayName(evidence.athlete, locale)} · {typeLabel(evidence.documentType)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate(locale, evidenceStatusKeys[evidence.verificationStatus])} ·{' '}
                {formatCalendarDate(evidence.validFrom, locale)}
                {' – '}
                {evidence.validTo === null
                  ? translate(locale, 'evidence.openEnded')
                  : formatCalendarDate(evidence.validTo, locale)}
                {evidence.issuer === null
                  ? ''
                  : ` · ${translate(locale, 'evidence.issuer')}: ${evidence.issuer}`}
                {evidence.reference === null
                  ? ''
                  : ` · ${translate(locale, 'evidence.reference')}: ${evidence.reference}`}
              </Typography>
              {evidence.decisionNote !== null && (
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'evidence.decisionNote')}: {evidence.decisionNote}
                </Typography>
              )}
              {evidence.canManage &&
                evidence.verificationStatus !== 'rejected' &&
                open === null && (
                  <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {evidence.verificationStatus === 'pending' && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setPanel({ kind: 'verify', evidenceId: evidence.id, note: '' });
                          }}
                        >
                          {translate(locale, 'evidence.verify')}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setPanel({
                              kind: 'correct',
                              evidenceId: evidence.id,
                              form: evidenceCorrectionFormOf(evidence),
                            });
                          }}
                        >
                          {translate(locale, 'evidence.correct')}
                        </Button>
                      </>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setPanel({ kind: 'reject', evidenceId: evidence.id, reason: '' });
                      }}
                    >
                      {translate(
                        locale,
                        evidence.verificationStatus === 'pending'
                          ? 'evidence.reject'
                          : 'evidence.withdraw',
                      )}
                    </Button>
                  </Box>
                )}
              {open?.kind === 'verify' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    void command(
                      evidence,
                      { method: 'POST', action: 'verify', body: verifyRequest(open.note) },
                      'evidence.verify.done',
                    );
                  }}
                >
                  <Typography variant="body2">
                    {translate(locale, 'evidence.verify.hint')}
                  </Typography>
                  <TextField
                    size="small"
                    label={translate(locale, 'evidence.verify.note')}
                    value={open.note}
                    onChange={(event) => {
                      setPanel({ ...open, note: event.target.value });
                    }}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button type="submit" variant="contained" size="small">
                      {translate(locale, 'evidence.verify.submit')}
                    </Button>
                    {cancelButton}
                  </Box>
                </Stack>
              )}
              {open?.kind === 'reject' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (rejection !== null) {
                      void command(
                        evidence,
                        { method: 'POST', action: 'reject', body: rejection },
                        'evidence.reject.done',
                      );
                    }
                  }}
                >
                  <Typography variant="body2">
                    {translate(locale, 'evidence.reject.hint')}
                  </Typography>
                  <TextField
                    required
                    size="small"
                    label={translate(locale, 'evidence.reject.reason')}
                    value={open.reason}
                    onChange={(event) => {
                      setPanel({ ...open, reason: event.target.value });
                    }}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="error"
                      size="small"
                      disabled={rejection === null}
                    >
                      {translate(locale, 'evidence.reject.submit')}
                    </Button>
                    {cancelButton}
                  </Box>
                </Stack>
              )}
              {open?.kind === 'correct' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (correction !== null) {
                      void command(
                        evidence,
                        { method: 'PATCH', body: correction },
                        'evidence.correct.done',
                      );
                    }
                  }}
                >
                  <TextField
                    size="small"
                    label={translate(locale, 'evidence.field.issuer')}
                    value={open.form.issuer}
                    onChange={(event) => {
                      setPanel({ ...open, form: { ...open.form, issuer: event.target.value } });
                    }}
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                  />
                  <TextField
                    size="small"
                    label={translate(locale, 'evidence.field.reference')}
                    value={open.form.reference}
                    onChange={(event) => {
                      setPanel({ ...open, form: { ...open.form, reference: event.target.value } });
                    }}
                    slotProps={{ htmlInput: { maxLength: 100 } }}
                  />
                  <TextField
                    required
                    size="small"
                    type="date"
                    label={translate(locale, 'evidence.field.validFrom')}
                    value={open.form.validFrom}
                    onChange={(event) => {
                      setPanel({ ...open, form: { ...open.form, validFrom: event.target.value } });
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label={translate(locale, 'evidence.field.validTo')}
                    value={open.form.validTo}
                    onChange={(event) => {
                      setPanel({ ...open, form: { ...open.form, validTo: event.target.value } });
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="small"
                      disabled={correction === null}
                    >
                      {translate(locale, 'evidence.correct.submit')}
                    </Button>
                    {cancelButton}
                  </Box>
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
      {competition.canManage ? (
        <Stack
          component="form"
          key={formKey}
          spacing={2}
          sx={{ maxWidth: 480 }}
          onSubmit={(event) => {
            event.preventDefault();
            void record();
          }}
        >
          <Typography variant="subtitle1" component="h4">
            {translate(locale, 'evidence.create.title')}
          </Typography>
          <RemoteSelect
            label={translate(locale, 'evidence.field.athlete')}
            noMatchesText={translate(locale, 'evidence.noAthletes')}
            load={findAthletes}
            onSelect={(athleteId) => {
              setForm((current) => ({ ...current, athleteId }));
            }}
          />
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'evidence.field.type')}
            value={form.documentType}
            onChange={(event) => {
              const documentType = documentEvidenceTypes.find(
                (candidate) => candidate === event.target.value,
              );
              if (documentType !== undefined) setForm({ ...form, documentType });
            }}
          >
            {documentEvidenceTypes.map((documentType) => (
              <MenuItem key={documentType} value={documentType}>
                {translate(locale, evidenceTypeKeys[documentType])}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label={translate(locale, 'evidence.field.issuer')}
            value={form.issuer}
            onChange={(event) => {
              setForm({ ...form, issuer: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            size="small"
            label={translate(locale, 'evidence.field.reference')}
            value={form.reference}
            onChange={(event) => {
              setForm({ ...form, reference: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <TextField
            required
            size="small"
            type="date"
            label={translate(locale, 'evidence.field.validFrom')}
            value={form.validFrom}
            onChange={(event) => {
              setForm({ ...form, validFrom: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="date"
            label={translate(locale, 'evidence.field.validTo')}
            value={form.validTo}
            onChange={(event) => {
              setForm({ ...form, validTo: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={createRequest === null}>
              {translate(locale, 'evidence.submit')}
            </Button>
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
}
